import * as queryString from 'query-string';
import * as React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import MultiBackend, { TouchTransition } from 'react-dnd-multi-backend';
import TouchBackend from 'react-dnd-touch-backend';
import { connect } from 'react-redux';

import { LoginUrls } from '@tableau/shared-urls';

import { PKCERouter } from '../../../pkce/PKCERouter';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { AuthLevel, ServerService } from '../../../ts/services/ServerService';
import { SessionUserSelectors } from '../../selectors/SessionInfoSelectors';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { AuthenticateConnectionRouter } from '../Authenticate/AuthenticateConnectionRouter';
import { LoginErrorProvider } from '../Login/LoginErrorProvider';
import { LoginUrlsWrapper } from '../Login/LoginUrls';
import { RequestPasswordResetProvider } from '../Login/RequestPasswordResetProvider';
import { ResetPasswordProvider, ResetPasswordProviderProps } from '../Login/ResetPasswordProvider';
import { SignedOutProvider, SiteSamlSignedOutProvider } from '../Login/SignedOutProviders';
import { SignInProvider, SignInProviderProps } from '../Login/SignInProvider';
import { SitePickerProvider } from '../Login/SitePickerProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { Main } from './Main';
import { ProcessAndRemoveSamlQueryWrapper } from './ProcessAndRemoveSamlQueryWrapper';
import { RemoveTrailingSlashWrapper } from './RemoveTrailingSlashWrapper';
import { RouterError } from './RouterError';

export interface ResetPasswordRouteParams {
  readonly token: string;
}
export interface ResetPasswordRouteComponentProps extends RouteComponentProps<ResetPasswordRouteParams> {}

export interface RootSpaceOwnProps extends ReactDirectiveProps {}

export interface RootSpaceStateProps {
  readonly urls: SpacesSiteUrls;
  readonly isSessionMultiSite: boolean;
}

export type RootSpaceProps = RootSpaceOwnProps & RootSpaceStateProps;

export class RootSpaceComponent extends React.Component<RootSpaceProps> {

  private server: ServerService;
  public constructor(props: RootSpaceProps) {
    super(props);
    this.server = ServiceLocator.get<ServerService>('ServerService');
  }

  public render() {
    return (
      <DndProvider backend={MultiBackend} options={HTML5toTouch}>
        <Switch>
          <Route path={'/authenticate'} render={() => <AuthenticateConnectionRouter angularContext={this.props.angularContext} />}/>
          <Route path={'/pkce'} render={() => <PKCERouter />} />
          <Route path={LoginUrlsWrapper.getInstance().signinPath()} render={this.signinProvider()} />
          <Route path={LoginUrlsWrapper.getInstance().loginErrorPath()} render={() => <LoginErrorProvider {...this.props}/>} />
          <Route path={LoginUrlsWrapper.getInstance().signedOutPath()} render={() => <SignedOutProvider {...this.props}/>} />
          <Route path={LoginUrlsWrapper.getInstance().siteSamlSignedOutPath()} render={() => <SiteSamlSignedOutProvider {...this.props} />} />
          <Route exact path={LoginUrlsWrapper.getInstance().passwordResetWithTokenPath(':token')} render={this.resetPasswordProvider()} />
          <Route exact path={LoginUrlsWrapper.getInstance().passwordResetUserNameBasePath()} render={this.resetPasswordProvider()} />
          <Route exact path={LoginUrlsWrapper.getInstance().requestPasswordResetPath()} render={() => <RequestPasswordResetProvider {...this.props}/>} />
          {this.protectedRouteForSitePicker()}
          { this.server.isUserAuthorized(AuthLevel.Authenticated) &&
            <Route render={(routeProps: RouteComponentProps) =>
              <ProcessAndRemoveSamlQueryWrapper>
                <RemoveTrailingSlashWrapper>
                  <Main
                    angularContext={this.props.angularContext}
                    routeLocationPath={routeProps.location.pathname}
                    routeLocationSearch={routeProps.location.search}
                  />
                </RemoveTrailingSlashWrapper>
              </ProcessAndRemoveSamlQueryWrapper>
            }/>
          }
          <Route render={this.error}/>
        </Switch>
      </DndProvider>
    );
  }

  private resetPasswordProvider = (): (props: ResetPasswordRouteComponentProps) => JSX.Element => {
    return (props: ResetPasswordRouteComponentProps): JSX.Element => {
      const currentQueryValues = queryString.parse(props.location.search);
      const name = currentQueryValues[LoginUrls.usernameQueryKey()];
      const username = name != null ? encodeURIComponent(name) : name;
      const resetPasswordProviderProps: ResetPasswordProviderProps = {
        angularContext: this.props.angularContext,
        token: props.match.params.token,
        username
      };
      return <ResetPasswordProvider {...resetPasswordProviderProps} />;
    };
  }

  private signinProvider = (): (props: RouteComponentProps) => JSX.Element => {
    return (props: RouteComponentProps): JSX.Element => {
      const signinProps: SignInProviderProps = {
        routeLocationSearch: props.location.search,
        angularContext: this.props.angularContext,
      };

      return <SignInProvider {...signinProps}/>;
    };
  }

  private protectedRouteForSitePicker() {
    return <Route exact={true} path={LoginUrlsWrapper.getInstance().sitePath()} render={() => {
      if (this.server.isUserAuthorized(AuthLevel.Authenticated)) {
        return <SitePickerProvider angularContext={this.props.angularContext}/>;
      } else {
        return <Redirect to={LoginUrlsWrapper.getInstance().signinPath()} />;
      }
    }}/>;
  }

  private error = (): JSX.Element => {
    if (this.server.inSession()) {
      return <RouterError errorType={'default'} urls={this.props.urls}/>;
    } else {
      return <Redirect to={LoginUrlsWrapper.getInstance().signinPath()} />;
    }
  }
}

const HTML5toTouch = {
  backends: [
    {
      backend: HTML5Backend
    },
    {
      backend: TouchBackend,
      options: { enableMouseEvents: true, enableKeyboardEvents: true, delayTouchStart: 200 },
      transition: TouchTransition
    }
  ]
};

const mapStateToProps: MapStateToProps<RootSpaceStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state),
  isSessionMultiSite: SessionUserSelectors.isSessionMultiSite(state),
});

export const ConnectedRootSpace: React.ComponentClass<RootSpaceOwnProps>
  = connect(mapStateToProps, null, null, {pure: false})(RootSpaceComponent);
